import React, { Component, useEffect } from 'react';
import _ from 'lodash';
import { Col, Form, Row } from 'react-bootstrap';
import styles from 'components/transactionsShuttle/transactionsSimpleTable/TransactionSimpleDetail.module.css';
import { CallbackFn, useFormInput, ValidatorType } from 'hooks/useFormInput';
import { useTranslation, WithTranslation, withTranslation } from 'react-i18next';
import BzFormControlWithHook from 'components/forms/BzFormControlWithHook';
import { MockService } from 'types/rest/mockServiceTypes';
import { connect } from 'react-redux';
import { updateTransactionPriorityAction } from 'store/transactions/transactionsActions';
import { MockServiceTemplate } from 'store/mockServiceTemplates/mockServiceTemplateTypes';

type TransactionPriorityProps = {
  disabled: boolean;
  priority: number;
  context: MockService | MockServiceTemplate;
  onUpdateTransactionPriorityHandler: (priority: number) => void;
  transactionId?: number;
};

type AllProps = WithTranslation & TransactionPriorityProps & DispatchProps;

/**
 * Simple Transaction priority input field which is able to modify priority on backend via action.
 */
class TransactionPriority extends Component<AllProps> {
  state = {
    priorityValue: this.props.priority,
  };

  UNSAFE_componentWillReceiveProps(nextProps: Readonly<AllProps>): void {
    if (nextProps.priority !== this.state.priorityValue) {
      this.setState({ priorityValue: nextProps.priority });
    }
  }

  updatePriorityHandler = _.debounce((value: number) => {
    this.setState({ priorityValue: value }, () => {
      if (this.props.priority !== value && this.props.transactionId) {
        this.props.updateTransactionPriorityAction(value, this.props.transactionId, this.props.context);
      }
    });
  }, 800);

  render() {
    const { t } = this.props;
    return (
      <Row className="qa-txn-priority-input">
        <Col className={styles.titleMain}>
          <Form.Label className={styles.priorityLabel}>{t('txn.priority.name')}:</Form.Label>
        </Col>
        <Col>
          <TransactionPriorityBzmInput
            updatePriorityHandler={this.updatePriorityHandler}
            priority={this.state.priorityValue}
            disabled={this.props.disabled}
          />
        </Col>
      </Row>
    );
  }
}

type TransactionPriorityBzmInputProps = {
  priority: number;
  disabled: boolean;
  updatePriorityHandler?: (number: number) => void;
};

/**
 * Simple function component wrapper for BzFormControl with validation hook. The wrapper is needed because
 * BzFormControlWithHook can be used only within functional component.
 * @param props
 * @constructor
 */

const TransactionPriorityBzmInput = (props: TransactionPriorityBzmInputProps) => {
  const { t } = useTranslation('transactions');
  let priority = props.priority;

  useEffect(() => {
    if (priorityInput.value !== props.priority) {
      priorityInput.resetValue(props.priority);
    }
  }, [props.priority]);

  const priorityInput = useFormInput(
    priority,
    (fn: CallbackFn) => {
      if (fn.isValid && props.updatePriorityHandler) {
        priority = parseInt('' + fn.value);
        props.updatePriorityHandler(priority);
      }
    },
    { type: ValidatorType.MAX, value: 2147483647, invalidMessage: t('txn.priority.error.range') },
    { type: ValidatorType.MIN, value: 1, invalidMessage: t('txn.priority.error.range') },
    { type: ValidatorType.INTEGER, invalidMessage: t('txn.priority.error.range') },
    { type: ValidatorType.REQUIRED, invalidMessage: t('txn.priority.error.required') },
  );

  return (
    <React.Fragment>
      <Row>
        <Col>
          <div className="w-10 float-left">
            <BzFormControlWithHook
              useFormInputHook={priorityInput}
              type="text"
              name="priority.priorityValue"
              required={true}
              hideErrors={true}
              value={props.priority + ''}
              disabled={props.disabled}
            />
          </div>
        </Col>
      </Row>
      <span className="form-text text-muted">{t('txn.priority.help')}</span>
      {!priorityInput.valid && (priorityInput.dirty || priorityInput.validating) && (
        <div className="invalid-feedback">{priorityInput.invalidMessage}</div>
      )}
    </React.Fragment>
  );
};

// Bound with dispatch so we can reuse the promise created by createService call
const mapDispatchToProps = (dispatch: any) => ({
  updateTransactionPriorityAction: (
    priorityValue: number,
    transactionId: number,
    context: MockService | MockServiceTemplate,
  ) => dispatch(updateTransactionPriorityAction(priorityValue, transactionId, context)),
});

type DispatchProps = {
  updateTransactionPriorityAction: (
    priorityValue: number,
    transactionId: number,
    context: MockService | MockServiceTemplate,
  ) => Promise<void>;
};

export default withTranslation('transactions')(connect(null, mapDispatchToProps)(TransactionPriority));
